// Unified playback bridge — abstracts Connector (Tauri), Baton (iOS), and local playback.
// MiniPlayer and all UI code calls ONLY these functions — never connector.ts or baton directly.
//
// iOS PARITY: iOS is always a REMOTE client. It never scans or serves local files.
// All local library playback on iOS flows through BATON WebSocket signaling:
//   1. requestRemoteFile() → BATON requestLocalFile command → home Connector
//   2. Home Connector registers file with its proxy, replies with tunnel URL
//   3. iOS receives tunnel URL via handleRemoteFileEvent()
//   4. iOS plays the tunnel URL via AVPlayer (playUri path) — no Tauri IPC involved

import * as Connector from './connector';
import { useMaestroStore } from '../store/maestroStore';
import { useNowPlayingStore } from '../store/nowPlayingStore';
import api from '../services/api';
import { createCommand, createEvent } from '../protocols/BATON';
import type { RequestLocalFileCommand, LocalFileReadyEvent, LocalFileErrorEvent } from '../protocols/BATON';
import { isHomeLibraryHost } from '../services/localLibraryIpcGate';
import { handleLocalPlayFailure } from '../utils/localPlayFailureUI';
import { recordLocalPlaySuccess } from '../utils/unmountedDriveHeuristic';
import {
  isCloudAuthOrOfflineError,
  localFileResolveFailureMessage,
  shouldAttemptLocalConnectorResolve,
} from './localFileResolvePolicy';
import {
  finalizeLocalPlaybackUrl,
} from './localStreamHost';

export type { StreamHostKind } from './localStreamHost';
export {
  classifyStreamHostKind,
  emitLocalStreamHostKindDiagnostic,
  isLocalStreamUrl,
  resolveLocalRendererStreamUrl,
  rewriteLocalRendererStreamUrl,
} from './localStreamHost';

// ---------------------------------------------------------------------------
// URL Cache — avoids repeated round-trips for files already resolved.
// Tunnel/proxy URLs are valid for 5 minutes; we cache for 4.
// ---------------------------------------------------------------------------

const localFileUrlCache = new Map<string, { url: string; expiresAt: number }>();
const CACHE_TTL_MS = 4 * 60 * 1000;

export function getCachedFileUrl(filePath: string): string | null {
  const entry = localFileUrlCache.get(filePath);
  if (!entry) return null;
  if (Date.now() > entry.expiresAt) {
    localFileUrlCache.delete(filePath);
    return null;
  }
  return finalizeLocalPlaybackUrl(entry.url);
}

function cacheFileUrl(filePath: string, url: string): void {
  localFileUrlCache.set(filePath, {
    url: finalizeLocalPlaybackUrl(url),
    expiresAt: Date.now() + CACHE_TTL_MS,
  });
}

/**
 * Evict a cached URL by stream URL (called when audio playback gets 403/error).
 * Also accepts a file_path to evict directly.
 */
export function invalidateCachedUrl(streamUrlOrPath: string): void {
  // Direct path match
  if (localFileUrlCache.has(streamUrlOrPath)) {
    localFileUrlCache.delete(streamUrlOrPath);
    return;
  }
  // Scan for entries matching the stream URL
  for (const [path, entry] of localFileUrlCache) {
    if (entry.url === streamUrlOrPath) {
      localFileUrlCache.delete(path);
      return;
    }
  }
}

export function clearAllCachedFileUrls(): void {
  localFileUrlCache.clear();
}

export type BridgeMode = 'local' | 'connector' | 'ios-native' | 'baton';

export interface PlayUriParams {
  rendererId: string;
  uri: string;
  title: string;
  artist: string;
  album: string;
  formatId: number;
  durationSecs: number;
  artUrl?: string;
  startSeconds?: number;
}

export interface PlayLocalFileParams {
  filePath: string;
  rendererId: string;
  title: string;
  artist: string;
  album: string;
  durationSecs: number;
  artworkId?: string | null;
}

export type { PlayLocalFileResult } from './connector';

export interface PlaybackBridge {
  mode: BridgeMode;

  playUri(params: PlayUriParams): Promise<void>;
  playLocalFile?(params: PlayLocalFileParams): Promise<import('./connector').PlayLocalFileResult>;
  play(rendererId: string): Promise<void>;
  pause(rendererId: string): Promise<void>;
  stop(rendererId: string): Promise<void>;
  seek(rendererId: string, seconds: number): Promise<void>;
  setVolume(rendererId: string, volume: number): Promise<void>;
}

export function getBridgeMode(): BridgeMode {
  if (Connector.isConnectorAvailable()) return 'connector';
  if (typeof window !== 'undefined' && (window as any).__maestroNative?.isNativeApp) return 'ios-native';
  if (typeof window !== 'undefined' && 'maestroBridge' in window) return 'baton';
  return 'local';
}

export function isBridgeAvailable(): boolean {
  return getBridgeMode() !== 'local';
}

export function isNativeBridgeMode(): boolean {
  const m = getBridgeMode();
  return m === 'connector' || m === 'ios-native';
}

export function createBridge(): PlaybackBridge | null {
  const mode = getBridgeMode();

  if (mode === 'connector') {
    return {
      mode: 'connector',
      async playUri(p) {
        const store = useMaestroStore.getState();
        const activeId = store.discovery?.activeZone?.id || store.activeConnectorRenderer?.id || null;
        if (activeId && p.rendererId !== activeId) {
          console.warn(`[Bridge] playUri aborted — renderer mismatch: called for ${p.rendererId}, active is ${activeId}`);
          return;
        }
        console.log('[Bridge] forwarding playUri to Connector with format_id:', p.formatId, 'startSeconds:', p.startSeconds ?? 'none');
        await Connector.playUri(p.rendererId, p.uri, p.title, p.artist, p.album, p.formatId, p.durationSecs, p.artUrl, p.startSeconds);
      },
      // iOS PARITY: Local file playback routes through Connector's HTTP proxy.
      // iOS will use the same proxy URL obtained via BATON signaling.
      async playLocalFile(p) {
        return Connector.playLocalFile(
          p.filePath,
          p.rendererId,
          p.title,
          p.artist,
          p.album,
          p.durationSecs,
          p.artworkId ?? null,
        );
      },
      async play(id) { await Connector.play(id); },
      async pause(id) { await Connector.pause(id); },
      async stop(id) { await Connector.stop(id); },
      async seek(id, secs) { await Connector.seek(id, secs); },
      async setVolume(id, vol) { await Connector.setVolume(id, vol); },
    };
  }

  if (mode === 'ios-native') {
    const native = (window as any).__maestroNative;
    const np = useNowPlayingStore.getState;
    return {
      mode: 'ios-native',
      async playUri(p) {
        console.log('[Bridge:iOS] playUri →', p.rendererId, p.title, 'format:', p.formatId);
        native?.postMessage('playUri', {
          rendererId: p.rendererId,
          uri: p.uri,
          title: p.title,
          artist: p.artist,
          album: p.album,
          formatId: p.formatId,
          durationSecs: p.durationSecs,
          artUrl: p.artUrl || '',
          startSeconds: p.startSeconds,
        });
        np().setStatus('playing');
      },
      async play(id) {
        console.log('[Bridge:iOS] play →', id);
        native?.postMessage('sendCommand', { deviceId: id, command: 'play' });
        np().setStatus('playing');
      },
      async pause(id) {
        console.log('[Bridge:iOS] pause →', id);
        native?.postMessage('sendCommand', { deviceId: id, command: 'pause' });
        np().setStatus('paused');
      },
      async stop(id) {
        console.log('[Bridge:iOS] stop →', id);
        native?.postMessage('sendCommand', { deviceId: id, command: 'stop' });
        np().setStatus('idle');
      },
      async seek(id, secs) {
        console.log('[Bridge:iOS] seek →', id, Math.floor(secs));
        native?.postMessage('seek', { deviceId: id, seconds: Math.floor(secs) });
      },
      async setVolume(id, vol) {
        console.log('[Bridge:iOS] setVolume →', id, Math.round(vol));
        native?.postMessage('setVolume', { deviceId: id, level: Math.round(vol) });
      },
    };
  }

  if (mode === 'baton') {
    const send = (type: string, payload: object) =>
      (window as any).maestroBridge?.send({ type, ...payload });

    return {
      mode: 'baton',
      async playUri(p) { send('playUri', p); },
      async play(id) { send('play', { rendererId: id }); },
      async pause(id) { send('pause', { rendererId: id }); },
      async stop(id) { send('stop', { rendererId: id }); },
      async seek(id, secs) { send('seek', { rendererId: id, seconds: secs }); },
      async setVolume(id, vol) { send('setVolume', { rendererId: id, volume: vol }); },
    };
  }

  return null;
}

// ---------------------------------------------------------------------------
// Remote Library Access
// ---------------------------------------------------------------------------

type PendingFileRequest = {
  resolve: (url: string) => void;
  reject: (err: Error) => void;
  timer: ReturnType<typeof setTimeout>;
};

const pendingFileRequests = new Map<string, PendingFileRequest>();

// ---------------------------------------------------------------------------
// Fire-and-forget dispatch pattern (non-blocking)
// ---------------------------------------------------------------------------

type PendingLocalDispatch = {
  trackId: string;
  track: any;
  timer: ReturnType<typeof setTimeout>;
};

const pendingLocalDispatches = new Map<string, PendingLocalDispatch>();

/**
 * Fire-and-forget: resolve a local file path to a playable URL and start playback.
 *
 * Resolution order (fastest first):
 *   1. URL cache → instant (~0ms)
 *   2. Connector IPC (when Tauri present + file is local) → ~50ms
 *   3. Direct tunnel fetch (remote peer with known tunnel URL) → ~200ms
 *   4. BATON WebSocket relay (fallback for remote files) → ~2-8s
 *
 * After successful resolution, caches the URL and prefetches the next
 * few queue tracks in the background so track advance is instant.
 *
 * iOS PARITY: iOS has no Connector, so it takes path #3 or #4.
 */
export function dispatchLocalFileRequest(
  filePath: string,
  trackId: string,
  track: any,
  ws: WebSocket | null,
  machineId?: string,
): void {
  const store = useMaestroStore.getState();

  // ── 1. Cache hit → instant playback ──
  const cached = getCachedFileUrl(filePath);
  if (cached) {
    console.log('[LocalFile] Cache HIT:', filePath.split('/').pop());
    import('../handlers/ClientTransportHandler').then(({ getClientTransport }) => {
      const s = useMaestroStore.getState();
      if (s.currentTrack?.id === trackId || s.currentTrack?.file_path === filePath) {
        getClientTransport().playLocalStream({ ...track, streamUrl: cached });
      }
    });
    prefetchNextLocalTracks(ws, machineId);
    return;
  }

  // ── 2. Connector IPC first (#807) — machine_id must not skip on-disk files.
  // iOS PARITY: iOS never enters this branch — isConnectorAvailable() is false.
  if (shouldAttemptLocalConnectorResolve(Connector.isConnectorAvailable())) {
    console.log('[LocalFile] Connector IPC path:', filePath.split('/').pop());
    Connector.registerFileUrl(filePath).then((url) => {
      cacheFileUrl(filePath, url);
      const s = useMaestroStore.getState();
      if (s.currentTrack?.id === trackId || s.currentTrack?.file_path === filePath) {
        import('../handlers/ClientTransportHandler').then(({ getClientTransport }) => {
          getClientTransport().playLocalStream({ ...track, streamUrl: url });
        });
      }
      prefetchNextLocalTracks(ws, machineId);
    }).catch((err) => {
      const errMsg = typeof err === 'string' ? err : err?.message || '';
      console.warn('[LocalFile] Connector IPC failed — trying cloud second:', errMsg);
      tryTunnelFallback(filePath, trackId, track, ws, machineId);
    });
    return;
  }

  // ── 3. Direct tunnel fetch (remote peer with known tunnel URL) ──
  const tunnelUrl = track?._source_tunnel_url;
  if (tunnelUrl && machineId) {
    console.log('[LocalFile] Direct tunnel path:', filePath.split('/').pop());
    fetchFromTunnel(tunnelUrl, filePath, machineId).then((url) => {
      cacheFileUrl(filePath, url);
      const s = useMaestroStore.getState();
      if (s.currentTrack?.id === trackId || s.currentTrack?.file_path === filePath) {
        import('../handlers/ClientTransportHandler').then(({ getClientTransport }) => {
          getClientTransport().playLocalStream({ ...track, streamUrl: url });
        });
      }
      prefetchNextLocalTracks(ws, machineId);
    }).catch((err) => {
      console.warn('[LocalFile] Direct tunnel failed, falling back to BATON:', err?.message || err);
      if (ws && ws.readyState === WebSocket.OPEN) {
        sendBatonFileRequest(filePath, trackId, track, ws, machineId);
      } else {
        store.setPlaybackState('stopped');
      }
    });
    return;
  }

  // ── 4. BATON WebSocket relay (remote files or no Connector) ──
  if (!ws || ws.readyState !== WebSocket.OPEN) {
    console.error('[LocalFile] No BATON connection — cannot request local file');
    store.setPlaybackState('stopped');
    return;
  }

  sendBatonFileRequest(filePath, trackId, track, ws, machineId);
}

/**
 * When a local file isn't found by the Connector, query Mycelium for ALL
 * registered tunnel URLs and try each one until we find the file. This handles
 * the case where the NAS/library is mounted on a different machine.
 */
async function tryTunnelFallback(
  filePath: string,
  trackId: string,
  track: any,
  ws: WebSocket | null,
  machineId?: string,
): Promise<void> {
  try {
    // Route through Mycelium server-side to avoid CORS — Mycelium tries all
    // registered tunnels and returns the first successful stream URL.
    console.log('[LocalFile] Resolving via Mycelium server-side proxy');
    const data = await api.post('/api/library/resolve-file', {
      file_path: filePath,
      ...(machineId ? { machine_id: machineId } : {}),
    });

    if (data?.url) {
      cacheFileUrl(filePath, data.url);
      console.log('[LocalFile] Server-side resolve succeeded via', data.machine_id);
      const s = useMaestroStore.getState();
      if (s.currentTrack?.id === trackId || s.currentTrack?.file_path === filePath) {
        const { getClientTransport } = await import('../handlers/ClientTransportHandler');
        getClientTransport().playLocalStream({ ...track, streamUrl: data.url });
      }
      prefetchNextLocalTracks(ws, machineId);
      return;
    }
  } catch (err: any) {
    console.warn('[LocalFile] Server-side resolve failed:', err?.message || err);
  }

  // Final fallback: BATON relay
  if (ws && ws.readyState === WebSocket.OPEN) {
    sendBatonFileRequest(filePath, trackId, track, ws, machineId);
  } else {
    const s = useMaestroStore.getState();
    if (s.currentTrack?.id === trackId || s.currentTrack?.file_path === filePath) {
      s.setPlaybackState('stopped');
    }
  }
}

function sendBatonFileRequest(
  filePath: string,
  trackId: string,
  track: any,
  ws: WebSocket,
  machineId?: string,
): void {
  const requestId = `rlf_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;
  const userId = (typeof localStorage !== 'undefined'
    ? localStorage.getItem('maestro_user_id')
    : null) || 'default';

  const timer = setTimeout(() => {
    pendingLocalDispatches.delete(requestId);
    console.error('[LocalFile] Timeout: home library did not respond within 10 seconds');
    const s = useMaestroStore.getState();
    if (s.currentTrack?.id === trackId || s.currentTrack?.file_path === filePath) {
      s.setPlaybackState('stopped');
    }
  }, 10_000);

  pendingLocalDispatches.set(requestId, { trackId, track, timer });

  const msg = createCommand('library', {
    cmd: 'requestLocalFile',
    requestId,
    filePath,
    userId,
    machineId,
    trackId,
  } as RequestLocalFileCommand);
  ws.send(JSON.stringify(msg));
  console.log('[LocalFile] BATON request sent:', filePath.split('/').pop());
}

/**
 * Directly call a remote peer's tunnel to register a file and get a signed stream URL.
 * This bypasses the BATON relay for faster resolution (~200ms vs 2-8s).
 */
async function fetchFromTunnel(
  tunnelUrl: string,
  filePath: string,
  machineId: string,
): Promise<string> {
  const resp = await fetch(`${tunnelUrl}/api/register-file`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ file_path: filePath, machine_id: machineId }),
  });
  if (!resp.ok) throw new Error(`Tunnel register-file ${resp.status}`);
  const data = await resp.json();
  if (!data?.url) throw new Error('No URL in tunnel response');
  return data.url;
}

// ---------------------------------------------------------------------------
// Prefetch — resolve URLs for upcoming queue tracks in the background
// ---------------------------------------------------------------------------

let prefetchInProgress = false;

export function prefetchNextLocalTracks(ws: WebSocket | null, machineId?: string): void {
  if (prefetchInProgress) return;
  prefetchInProgress = true;
  setTimeout(() => { prefetchInProgress = false; }, 2000);

  try {
    const store = useMaestroStore.getState();
    const { queue, queueIndex } = store;
    const upcoming = queue.slice(queueIndex + 1, queueIndex + 4);

    for (const item of upcoming) {
      const t = item?.track;
      if (!t?.file_path || t?.source !== 'local_library') continue;
      if (getCachedFileUrl(t.file_path)) continue;

      if (shouldAttemptLocalConnectorResolve(Connector.isConnectorAvailable())) {
        Connector.registerFileUrl(t.file_path)
          .then(url => { cacheFileUrl(t.file_path, url); console.log('[Prefetch] cached:', t.title); })
          .catch(() => {});
        continue;
      }

      // Remote viewer: prefetch via Mycelium (server-side tunnel resolve).
      if (!isHomeLibraryHost() && t.file_path) {
        api.post('/api/library/resolve-file', {
          file_path: t.file_path,
          ...(t.machine_id ? { machine_id: t.machine_id } : {}),
        })
          .then((data) => {
            if (data?.url) {
              cacheFileUrl(t.file_path, data.url);
              console.log('[Prefetch] Mycelium cached:', t.title);
            }
          })
          .catch(() => {});
        continue;
      }

      if (t._source_tunnel_url && t.machine_id) {
        fetchFromTunnel(t._source_tunnel_url, t.file_path, t.machine_id)
          .then(url => { cacheFileUrl(t.file_path, url); console.log('[Prefetch] tunnel cached:', t.title); })
          .catch(() => {});
        continue;
      }

      if (ws && ws.readyState === WebSocket.OPEN) {
        const userId = (typeof localStorage !== 'undefined' ? localStorage.getItem('maestro_user_id') : null) || 'default';
        requestRemoteFile(t.file_path, userId, ws, t.machine_id)
          .then(url => { cacheFileUrl(t.file_path, url); console.log('[Prefetch] cached:', t.title); })
          .catch(() => {});
      }
    }
  } catch { /* best effort */ }
}

/**
 * Returns true if the id refers to a local library entity (stable id from Connector SQLite).
 */
export function isLocalTrackId(id: unknown): boolean {
  const s = String(id || '');
  if (s.startsWith('local:track:')) return true;
  if (s.startsWith('local:album:')) return true;
  if (/^[0-9a-f]{16}$/i.test(s)) return true;
  return false;
}

/**
 * Returns true if the track should use local file playback (not Qobuz stream resolution).
 */
export function isLocalLibraryTrack(track: any): boolean {
  if (!track) return false;
  if (track.source === 'local_library') return true;
  const id = String(track.id || track.trackId || track.serviceTrackId || '');
  if (isLocalTrackId(id)) return true;
  if (track.file_path && !track.qobuzTrackId && !track.serviceTrackId) return true;
  return false;
}

/**
 * Resolve a local library file_path to a playable HTTP URL.
 *
 * Engine-first (#807): Connector IPC on this disk first, Mycelium resolve-file
 * second. A cloud 401/offline must never fail playback by itself.
 *
 * Platform routing:
 * - Connector (Tauri desktop): registers the file with the local proxy via IPC,
 *   returns a signed LAN/tunnel URL immediately.
 * - iOS / web (no Tauri): sends a BATON requestLocalFile command over WebSocket
 *   to the home Connector, which replies with a tunnel URL. Requires an active
 *   BATON connection.
 *
 * iOS PARITY: iOS uses this function exclusively for local library playback.
 * The returned URL points to the home Connector's Cloudflare Tunnel — iOS plays
 * it directly via AVPlayer. No Tauri IPC is ever involved on iOS.
 */
export async function resolveLocalFileUrl(
  filePath: string,
  ws: WebSocket | null,
  machineId?: string,
): Promise<string> {
  if (Connector.isConnectorAvailable()) {
    await Connector.ensureConnectorInfo();
  }

  const cached = getCachedFileUrl(filePath);
  if (cached) return cached;

  const connectorAvailable = Connector.isConnectorAvailable();
  let localError: unknown;

  // Path 1: Connector IPC first (#807). machine_id must not skip on-disk files.
  if (shouldAttemptLocalConnectorResolve(connectorAvailable)) {
    try {
      const url = await Connector.registerFileUrl(filePath);
      cacheFileUrl(filePath, url);
      return getCachedFileUrl(filePath)!;
    } catch (err) {
      localError = err;
      console.warn('[LocalFile] registerFileUrl failed — trying Mycelium second:', err);
    }
  }

  // Path 2a: Mycelium — never fatal on 401/offline.
  let cloudError: unknown;
  const tryMyceliumResolve = async (mid?: string) => {
    const data = await api.post('/api/library/resolve-file', {
      file_path: filePath,
      ...(mid ? { machine_id: mid } : {}),
    });
    if (data?.url) {
      cacheFileUrl(filePath, data.url as string);
      console.log('[LocalFile] Mycelium resolve-file OK via', data.machine_id || mid || 'any');
      return getCachedFileUrl(filePath)!;
    }
    return null;
  };

  try {
    const url = await tryMyceliumResolve(machineId);
    if (url) return url;
  } catch (err: unknown) {
    cloudError = err;
    console.warn('[LocalFile] Mycelium resolve-file failed:', (err as { message?: string })?.message || err);
  }

  if (machineId) {
    try {
      const url = await tryMyceliumResolve(undefined);
      if (url) return url;
    } catch (err: unknown) {
      cloudError = err;
      console.warn('[LocalFile] Mycelium resolve-file (all machines) failed:', (err as { message?: string })?.message || err);
    }
  }

  // Cloud 401/offline: retry local once (boot race) — never fail on 401 alone.
  if (connectorAvailable && isCloudAuthOrOfflineError(cloudError)) {
    try {
      const url = await Connector.registerFileUrl(filePath);
      cacheFileUrl(filePath, url);
      console.warn('[LocalFile] cloud 401 ignored — local Connector resolve succeeded');
      return getCachedFileUrl(filePath)!;
    } catch (err) {
      localError = err;
    }
  }

  // Path 2b: BATON WebSocket relay (iOS / remote with active session).
  if (!ws || ws.readyState !== WebSocket.OPEN) {
    throw new Error(localFileResolveFailureMessage({
      connectorAvailable,
      localError,
      cloudError,
      batonAvailable: false,
    }));
  }
  const userId = (typeof localStorage !== 'undefined'
    ? localStorage.getItem('maestro_user_id')
    : null) || 'default';
  const url = await requestRemoteFile(filePath, userId, ws, machineId);
  cacheFileUrl(filePath, url);
  return getCachedFileUrl(filePath)!;
}

/**
 * Called by a REMOTE client to request a file from the home Connector.
 * Sends a requestLocalFile BATON command and waits for a localFileReady event.
 *
 * iOS PARITY: This is the primary path for iOS local library playback.
 * Uses standard WebSocket — no Tauri IPC involved.
 *
 * @param machineId — identifies which Connector machine holds the file.
 *   Mycelium uses this to look up the correct tunnel URL when the user has
 *   multiple machines.
 */
export function requestRemoteFile(
  filePath: string,
  userId: string,
  ws: WebSocket | null,
  machineId?: string,
): Promise<string> {
  return new Promise((resolve, reject) => {
    if (!ws || ws.readyState !== WebSocket.OPEN) {
      reject(new Error('No BATON connection'));
      return;
    }

    const requestId = `rlf_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;

    const timer = setTimeout(() => {
      pendingFileRequests.delete(requestId);
      reject(new Error('Home library unavailable — no response within 5 seconds'));
    }, 5_000);

    pendingFileRequests.set(requestId, {
      resolve,
      reject,
      timer,
    });

    const msg = createCommand('library', {
      cmd: 'requestLocalFile',
      requestId,
      filePath,
      userId,
      machineId,
    } as RequestLocalFileCommand);
    ws.send(JSON.stringify(msg));
  });
}

/**
 * Called when a localFileReady or localFileError event arrives via BATON.
 *
 * Handles two patterns:
 * 1. Dispatch pattern (fire-and-forget): calls playLocalStream() directly
 * 2. Promise pattern (legacy): resolves/rejects the pending Promise
 *
 * iOS PARITY: On iOS, this resolves the tunnel URL that AVPlayer will stream from.
 * The event arrives via the same BATON WebSocket used by all platforms.
 */
export function handleRemoteFileEvent(event: LocalFileReadyEvent | LocalFileErrorEvent): void {
  const requestId = event.requestId;

  // --- Dispatch pattern (non-blocking) ---
  const dispatch = pendingLocalDispatches.get(requestId);
  if (dispatch) {
    pendingLocalDispatches.delete(requestId);
    clearTimeout(dispatch.timer);

    if (event.event === 'localFileReady') {
      const url = (event as LocalFileReadyEvent).url;
      if (dispatch.track?.file_path) {
        cacheFileUrl(dispatch.track.file_path, url);
        recordLocalPlaySuccess(dispatch.track.file_path);
      }
      import('../handlers/ClientTransportHandler').then(({ getClientTransport }) => {
        const s = useMaestroStore.getState();
        if (s.currentTrack?.id === dispatch.trackId || s.currentTrack?.file_path === dispatch.track?.file_path) {
          const fullTrack = { ...dispatch.track, streamUrl: url };
          getClientTransport().playLocalStream(fullTrack);
        }
        prefetchNextLocalTracks(getClientTransport().getWebSocket(), dispatch.track?.machine_id);
      });
    } else {
      const errorMsg = (event as LocalFileErrorEvent).error;
      console.error('[LocalFile] Error from daemon:', errorMsg);
      const s = useMaestroStore.getState();
      if (s.currentTrack?.id === dispatch.trackId || s.currentTrack?.file_path === dispatch.track?.file_path) {
        s.setPlaybackState('stopped');
        handleLocalPlayFailure(
          errorMsg,
          dispatch.track?.title || 'This track',
          dispatch.track?.file_path,
        );
      }
    }
    return;
  }

  // --- Promise pattern (used by FullAlbumOverlay, auto-advance) ---
  const pending = pendingFileRequests.get(requestId);
  if (!pending) return;

  pendingFileRequests.delete(requestId);
  clearTimeout(pending.timer);

  if (event.event === 'localFileReady') {
    pending.resolve((event as LocalFileReadyEvent).url);
  } else {
    pending.reject(new Error((event as LocalFileErrorEvent).error));
  }
}

/**
 * Called on the HOME CONNECTOR client when a requestLocalFile command arrives.
 * Registers the file with the local proxy and sends back a localFileReady event.
 *
 * iOS PARITY: This function ONLY runs on the home Connector (Tauri desktop).
 * iOS never executes this — it is always the requesting side, never the serving side.
 * The guard `Connector.isConnectorAvailable()` ensures no Tauri IPC on iOS/web.
 */
export async function handleIncomingFileRequest(
  payload: { requestId: string; filePath: string; userId: string; machineId?: string },
  ws: WebSocket | null,
): Promise<void> {
  if (!Connector.isConnectorAvailable()) return;
  if (!ws || ws.readyState !== WebSocket.OPEN) return;

  try {
    const url = await Connector.registerFileUrl(payload.filePath);
    const expiresAt = Date.now() + 5 * 60 * 1000;
    const msg = createEvent('library', {
      event: 'localFileReady',
      requestId: payload.requestId,
      url,
      expiresAt,
    } as LocalFileReadyEvent);
    ws.send(JSON.stringify(msg));
  } catch (err: any) {
    const msg = createEvent('library', {
      event: 'localFileError',
      requestId: payload.requestId,
      error: err?.message || 'Failed to register file',
    } as LocalFileErrorEvent);
    ws.send(JSON.stringify(msg));
  }
}
